/**
 * AuthContext – MSAL + Grom token auth, login flows (web / Teams), and token refresh scheduling.
 */

import { createContext, useContext, ReactNode, useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../lib/authConfig';
import { TokenService } from '../services/tokenService';
import { TokenRequest } from '../types/auth';
import { STORAGE_KEYS } from '@/constants/storage';
import { app } from '@microsoft/teams-js';
import { useTeamApp } from './TeamAppContext';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { clearAllFilterCaches } from '@/lib/filterCache';
import { getHttpErrorKind } from '@/utils/httpErrors';
import { useTokenRefreshSchedule } from '@/hooks/useTokenRefreshSchedule';
import { redirectToUnauthorize } from '@/utils/authRedirect';

// -----------------------------------------------------------------------------
// Types & context
// -----------------------------------------------------------------------------

interface AuthContextType {
    isAuthenticated: boolean;
    login: () => Promise<void>;
    user: AccountInfo | null;
    microsoftToken: string | null;
    gromToken: string | null;
    getGromToken: (msToken: string) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -----------------------------------------------------------------------------
// Helpers (outside provider)
// -----------------------------------------------------------------------------

/**
 * Removes Grom token and account info from localStorage. Ignores storage errors.
 */
function clearAuthStorage() {
    try {
        localStorage.removeItem(STORAGE_KEYS.GROM_TOKEN);
        localStorage.removeItem(STORAGE_KEYS.ACCOUNT_INFO);
    } catch {
        // ignore storage errors
    }
}

// -----------------------------------------------------------------------------
// AuthProvider
// -----------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
    // ----- Hooks & state -----
    const { instance, accounts, inProgress } = useMsal();
    const { isInTeams, msTeamsAuthToken } = useTeamApp();
    const tokenService = useMemo(() => new TokenService(), []);

    const [microsoftToken, setMicrosoftToken] = useState<string | null>(null);
    const [gromToken, setGromToken] = useState<string | null>(null);

    const loginRef = useRef<() => Promise<void>>();
    const isRefreshingRef = useRef(false);
    // Guards instance.loginRedirect() against a React 18 StrictMode double-invoke of the mount
    // effect in ProtectedRoute: both invocations read `inProgress` from the same stale render
    // (MSAL's browser-storage "interaction in progress" flag is set synchronously inside
    // loginRedirect(), before React's `inProgress` state has re-rendered), so the second call's
    // `inProgress === InteractionStatus.None` check still passes and MSAL itself throws
    // BrowserAuthError: interaction_in_progress. Reset only on failure — success navigates away.
    const isLoginRedirectInFlightRef = useRef(false);

    // ----- Auth state helpers -----

    /**
     * Clears all auth state (React state + refs + localStorage) and redirects to Microsoft login.
     * Used when silent token acquisition fails or when we need to force interactive login.
     */
    const clearAuthStateAndRedirect = useCallback(async () => {
        setMicrosoftToken(null);
        setGromToken(null);
        clearAuthStorage();
        await instance.loginRedirect(loginRequest);
    }, [instance]);

    /**
     * Exchanges Microsoft token for Grom token via TokenService. Updates gromToken state and localStorage.
     * On error: transient → rethrow; 401/403 → clear storage + redirect to unauthorize; other → log and rethrow.
     */
    const getGromToken = useCallback(async (msToken: string) => {
        try {
            const tokenRequest: TokenRequest = {
                clientId: import.meta.env.VITE_CLIENT_ID,
                productId: import.meta.env.VITE_PRODUCT_ID
            };
            const token = await tokenService.getToken(msToken, tokenRequest);
            setGromToken(token);
            localStorage.setItem(STORAGE_KEYS.GROM_TOKEN, token);
            return token;
        } catch (error) {
            console.error('Failed to get Grom token:', error);
            const kind = getHttpErrorKind(error);

            if (kind === 'transient') {
                throw error;
            }

            if (kind === 'auth' || kind === 'forbidden') {
                clearAuthStorage();
                redirectToUnauthorize();
            }
            throw error;
        }
    }, [tokenService]);

    // ----- Login flow (MSAL + Grom) -----

    /**
     * Tries to get a fresh MSAL token silently. On failure (e.g. interaction required), clears auth and redirects to login; returns null.
     */
    const tryAcquireMsalTokenSilent = useCallback(async (account: AccountInfo) => {
        try {
            return await instance.acquireTokenSilent({
                ...loginRequest,
                account,
                forceRefresh: true,
            });
        } catch (msalError) {
            console.warn('MSAL acquireTokenSilent failed, forcing interactive login:', msalError);
            await clearAuthStateAndRedirect();
            return null;
        }
    }, [instance, clearAuthStateAndRedirect]);

    /**
     * Applies MSAL response: sets Microsoft token + account info, fetches Grom token, clears filter caches.
     * Called after silent or interactive MSAL login.
     */
    const applyMsalResponseAndGetGromToken = useCallback(async (response: { idToken: string; account: { name?: string; username?: string } }) => {
        setMicrosoftToken(response.idToken);
        const accountInfo = { name: response.account.name ?? '', username: response.account.username ?? '' };
        console.log('Logged in account:', accountInfo.username);
        localStorage.setItem(STORAGE_KEYS.ACCOUNT_INFO, JSON.stringify(accountInfo));
        await getGromToken(response.idToken);
        clearAllFilterCaches();
    }, [getGromToken]);

    /**
     * Runs silent token acquisition for the given account; if successful, applies response and gets Grom token.
     * Sets isRefreshingRef for the duration to avoid duplicate refresh calls.
     */
    const handleSilentTokenAcquisition = useCallback(async (account: AccountInfo) => {
        isRefreshingRef.current = true;
        try {
            const response = await tryAcquireMsalTokenSilent(account);
            if (response === null) return;
            await applyMsalResponseAndGetGromToken(response);
        } finally {
            isRefreshingRef.current = false;
        }
    }, [tryAcquireMsalTokenSilent, applyMsalResponseAndGetGromToken]);

    /**
     * Web login: if there are accounts, tries silent acquisition; otherwise triggers MSAL loginRedirect when no interaction in progress.
     */
    const handleWebLogin = async () => {
        try {
            if (!instance.getActiveAccount()) {
                await instance.initialize();
            }

            if (accounts.length > 0) {
                if (inProgress !== InteractionStatus.None) return;
                await handleSilentTokenAcquisition(accounts[0]);
            } else if (inProgress === InteractionStatus.None && !microsoftToken && !isLoginRedirectInFlightRef.current) {
                isLoginRedirectInFlightRef.current = true;
                try {
                    await instance.loginRedirect(loginRequest);
                } catch (redirectError) {
                    isLoginRedirectInFlightRef.current = false;
                    throw redirectError;
                }
            }
        } catch (error) {
            console.error('Error in handleWebLogin:', error);
            throw error;
        }
    };

    /**
     * Teams login: uses msTeamsAuthToken, stores account from Teams context, fetches Grom token, clears filter caches.
     */
    const handleTeamsLogin = async () => {
        try {
            console.log("Running in Ms Teams App");
            setMicrosoftToken(msTeamsAuthToken);

            const context = await app.getContext();
            const user = context?.user;
            const accountInfo = {
                name: user?.displayName,
                username: user?.userPrincipalName,
            };
            console.log('Logged in account:', accountInfo.username);
            localStorage.setItem(STORAGE_KEYS.ACCOUNT_INFO, JSON.stringify(accountInfo));
            await getGromToken(msTeamsAuthToken);
            clearAllFilterCaches();
        } catch (error) {
            console.error("Error in handleTeamsLogin:", error);
            throw error;
        }
    };

    /**
     * Main login entry: delegates to handleTeamsLogin (in Teams) or handleWebLogin (web).
     */
    const login = useCallback(async () => {
        if (isInTeams) {
            await handleTeamsLogin();
        } else {
            await handleWebLogin();
        }
        // Intentionally partial deps: loginRef.current is used in timeouts so they always call the latest login.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInTeams, msTeamsAuthToken, accounts, inProgress, microsoftToken, instance]);

    /** Keeps loginRef in sync so timeouts and event handlers always call the latest login. */
    useEffect(() => {
        loginRef.current = login;
    }, [login]);

    useTokenRefreshSchedule(microsoftToken, loginRef, isRefreshingRef);

    // ----- Context value & render -----

    const value = useMemo(() => ({
        isAuthenticated: microsoftToken?.length > 0,
        login,
        user: accounts[0] || null,
        microsoftToken,
        gromToken,
        getGromToken
    }), [microsoftToken, accounts, gromToken, login, getGromToken]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// -----------------------------------------------------------------------------
// Hook
// -----------------------------------------------------------------------------

/**
 * Returns auth context. Must be used inside AuthProvider.
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
